<style lang="scss">
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/mixins";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/utils";

  .header {
    @include mixins.body1-bold();
  }

  .inputs {
    display: flex;
    justify-content: flex-start;
    gap: utils.in_rem(23px) utils.in_rem(26px);
    align-self: stretch;
    flex-wrap: wrap;

    .input {
      flex: 1 1 calc(50% - utils.in_rem(23px)); // Two inputs per row
      max-width: calc(50% - utils.in_rem(13px)); // Don't stretch lonely inputs
    }

    .large-input {
      flex: 1 1 100%; // stretch the whole width
    }
  }
</style>

<script>
  import { userStore } from "@sourcewhale/shared/src/svelte/api-stores/user-store";
  import MultiSelectDropdown from "@sourcewhale/shared/svelte-design-system/components/molecules/Dropdown/MultiSelectDropdown/MultiSelectDropdown.svelte";
  import SingleSelectDropdown from "@sourcewhale/shared/svelte-design-system/components/molecules/Dropdown/SingleSelectDropdown/SingleSelectDropdown.svelte";
  import TextInput from "@sourcewhale/shared/svelte-design-system/components/molecules/InputFields/TextInput.svelte";
  import TextInputWithChips from "@sourcewhale/shared/svelte-design-system/components/molecules/InputFields/TextInputWithChips.svelte";
  import { TAGS } from "../../../../../../assets/js/src/shared/tags";
  import { editContactStore } from "../../stores";
  import CompanyAutocompleteField from "./CompanyAutocompleteField.svelte";
  import CurrencyDropdown from "./components/CurrencyDropdown.svelte";
  import CurrentSalaryInput from "./components/CurrentSalaryInput.svelte";
  import ExpectedSalaryInput from "./components/ExpectedSalaryInput.svelte";
  import { helperTextStore, invalidItemStore } from "./stores";

  export let items = [];

  const tagItems = TAGS.map((x) => ({
    id: x.tagId,
    label: x.title,
  }));

  const relationshipItems = [
    { id: "", label: "None" },
    { id: "client", label: "Client" },
    { id: "prospect", label: "Prospect" },
    { id: "companyContact", label: "Company Contact" },
  ];

  const focusMethods = {};

  // Pre-filter items based on user permissions
  $: filteredItems = items.filter(
    (item) =>
      !item.restriction || item.restriction !== "hasCrm" || $userStore.hasCrm
  );

  const subscribe = () => {
    const destroy = invalidItemStore.subscribe((x) => {
      focusMethods[`${x.id}s`]?.();
    });
    return {
      destroy,
    };
  };

  // Reactive statement to clear invalidItemStore if
  // the value is no longer in editContactStore
  $: {
    const invalidItem = $invalidItemStore;
    if (invalidItem.type && invalidItem.value) {
      const itemsArray = $editContactStore[`${invalidItem.type}s`];
      if (!itemsArray || !itemsArray.includes(invalidItem.value)) {
        invalidItemStore.set({});
      }
    }
  }
</script>

<div class="header">
  <slot name="header" />
</div>
<div class="inputs" use:subscribe>
  {#each filteredItems as item}
    {#if item.type === "text"}
      <div class="input">
        <TextInput label={item.label} bind:value={$editContactStore[item.id]} />
      </div>
    {:else if item.type === "chip"}
      <div class="input">
        <TextInputWithChips
          label={item.label}
          bind:chips={$editContactStore[item.id]}
          invalidChips={[$invalidItemStore.value]}
          bind:focus={focusMethods[item.id]}
          helperText={$helperTextStore[item.id] || ""}
        />
        <!-- Backend only returns one invalid chip at a time -->
      </div>
    {:else if item.type === "freeText"}
      <div class="large-input">
        <TextInput
          label={item.label}
          kind="textarea"
          placeholder="Add comments here"
          bind:value={$editContactStore[item.id]}
        />
      </div>
    {:else if item.type === "tags"}
      <div class="input">
        <MultiSelectDropdown
          label={item.label}
          placeholder="Select"
          items={tagItems}
          bind:selectedItems={$editContactStore[item.id]}
        />
      </div>
    {:else if item.type === "relationship"}
      <div class="input">
        <SingleSelectDropdown
          label={item.label}
          tooltipMessage="Describes how this contact relates to your business."
          items={relationshipItems}
          bind:selected={$editContactStore[item.id]}
        />
      </div>
    {:else if item.type === "companyAutocomplete"}
      <div class="input">
        <CompanyAutocompleteField />
      </div>
    {:else if item.type === "currencyDropdown"}
      <div class="input">
        <CurrencyDropdown />
      </div>
    {:else if item.type === "currentSalary"}
      <div class="large-input">
        <CurrentSalaryInput />
      </div>
    {:else if item.type === "expectedSalary"}
      <div class="large-input">
        <ExpectedSalaryInput />
      </div>
    {/if}
  {/each}
</div>
